import { cleanLeaveReason, displayUsername } from '../../utils/userUtils';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LeaveApplication, LeaveApplicationResponse, LeaveStatus, UserLeaveBalanceSummary } from '../../interfaces/leave.interface';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { LeaveService } from '../../services/leave.service';
import { LeaveBalanceService } from '../../services/leave-balance.service';
import {
    Box,
    Button,
    Card,
    CardContent,
    Paper,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    Tooltip
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { format } from 'date-fns';
import LeaveForm from './LeaveForm';
import { useToast } from '../../context/ToastContext';

const LeaveApplicationList: React.FC = () => {
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [balanceSummary, setBalanceSummary] = useState<UserLeaveBalanceSummary | null>(null);
    const [loading, setLoading] = useState(true);
    // "Apply Leave" in the header search links here with ?apply=1: open the form straight away, then
    // drop the flag so a reload or Back does not open it again.
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFormOpen, setIsFormOpen] = useState(searchParams.get('apply') === '1');
    const toast = useToast();
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);

    useEffect(() => {
        fetchLeaveApplications();
        fetchBalance();
    }, []);

    useEffect(() => {
        if (searchParams.get('apply') !== '1') return;
        setIsFormOpen(true);
        setSearchParams(params => { params.delete('apply'); return params; }, { replace: true });
    }, [searchParams, setSearchParams]);

    const fetchLeaveApplications = async () => {
        try {
            const data = await LeaveService.getLeaveApplications();
            setLeaveData(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave applications');
        } finally {
            setLoading(false);
        }
    };

    const fetchBalance = async () => {
        try {
            const data = await LeaveBalanceService.getMyBalance();
            setBalanceSummary(data);
        } catch (err) {
            // Silently fail - balance display is optional
        }
    };

    const handleApplyLeave = () => {
        setIsFormOpen(true);
    };

    const handleCancelClick = (id: string) => {
        setSelectedLeaveId(id);
        setCancelConfirmOpen(true);
    };

    const handleCancelConfirm = async () => {
        if (!selectedLeaveId) return;

        try {
            await LeaveService.cancelLeaveApplication(selectedLeaveId);
            await fetchLeaveApplications();
            setCancelConfirmOpen(false);
            toast.showSuccess('Leave application cancelled');
        } catch (err) {
            toast.showApiError(err, 'Failed to cancel leave application');
        }
    };

    const handleCancelDialogClose = () => {
        setCancelConfirmOpen(false);
        setSelectedLeaveId(null);
    };

    const getStatusColor = (status: LeaveStatus) => {
        switch (status) {
            case LeaveStatus.Approved:
                return 'success';
            case LeaveStatus.Rejected:
                return 'error';
            case LeaveStatus.Cancelled:
                return 'warning';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString: string) => {
        return format(new Date(dateString), 'dd-MM-yyyy');
    };

    const columns: Column<LeaveApplication>[] = [
        { key: 'leaveType', header: 'Leave Type', priority: 1, helpKey: 'leave.mine.col.leaveType', render: leave => leave.leaveType },
        {
            key: 'reason', helpKey: 'leave.mine.col.reason', header: 'Reason', render: leave => (
                <Tooltip title={cleanLeaveReason(leave.leaveReason)} placement="top-start" arrow>
                    <Box
                        data-help="leave.mine.col.reason"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            maxWidth: 260,
                        }}
                    >
                        {cleanLeaveReason(leave.leaveReason)}
                    </Box>
                </Tooltip>
            ),
        },
        { key: 'applied', header: 'Application Date', render: leave => <Box data-help="leave.mine.col.applied" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.applicationDate)}</Box> },
        { key: 'start', header: 'Start Date', priority: 1, render: leave => <Box data-help="leave.mine.col.start" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.startDate)}</Box> },
        { key: 'end', header: 'End Date', render: leave => <Box data-help="leave.mine.col.end" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.endDate)}</Box> },
        { key: 'days', header: 'Days', helpKey: 'leave.mine.col.days', render: leave => leave.numberOfDays },
        {
            key: 'status', helpKey: 'leave.mine.col.status', header: 'Status', priority: 1, render: leave => (
                <Chip
                    data-help="leave.mine.col.status"
                    label={leave.leaveStatus}
                    color={getStatusColor(leave.leaveStatus) as any}
                    size="small"
                    sx={{ pointerEvents: 'none' }}
                />
            ),
        },
        {
            key: 'approver', helpKey: 'leave.mine.col.approver', header: 'Approver', render: leave => leave.actionedByName ? (
                <Box data-help="leave.mine.col.approver">
                    <Typography variant="body2">{leave.actionedByDisplayName || displayUsername(leave.actionedByName)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {leave.actionedAt ? format(new Date(leave.actionedAt), 'dd MMM yyyy, hh:mm a') : ''}
                    </Typography>
                </Box>
            ) : (
                <Typography data-help="leave.mine.col.approver" variant="body2" color="text.secondary">—</Typography>
            ),
        },
        {
            key: 'documents', helpKey: 'leave.mine.col.documents', header: 'Documents', render: leave => leave.documents && leave.documents.length > 0 ? (
                <Box data-help="leave.mine.col.documents">
                    {leave.documents.map((doc) => (
                        <Link
                            key={doc.id}
                            component="button"
                            variant="body2"
                            onClick={() => LeaveService.downloadDocument(doc.id, doc.originalFileName)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                        >
                            <AttachFileIcon fontSize="small" />
                            {doc.originalFileName}
                        </Link>
                    ))}
                </Box>
            ) : (
                <Typography data-help="leave.mine.col.documents" variant="body2" color="text.secondary">-</Typography>
            ),
        },
        {
            key: 'actions', header: 'Actions', helpKey: 'leave.mine.col.actions', role: 'actions', render: leave => leave.leaveStatus === LeaveStatus.Pending ? (
                <Button data-help="leave.cancel"
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleCancelClick(leave.id)}
                >
                    Cancel
                </Button>
            ) : null,
        },
    ];

    if (loading) return <Typography>Loading...</Typography>;
    if (!leaveData) return <Typography>No leave applications found</Typography>;

    return (
        <Box data-help-section="leave.myApplications" sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <Typography variant="h5">
                    Leave Applications
                </Typography>
                <Button data-help="leave.apply"
                    variant="contained"
                    color="primary"
                    onClick={handleApplyLeave}
                >
                    Apply Leave
                </Button>
            </Box>

            {balanceSummary && balanceSummary.balances.length > 0 && (
                <Box data-help-section="leave.mine.balances" sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, marginBottom: 3, flexWrap: 'wrap' }}>
                    {balanceSummary.balances.map((balance) => (
                        <Card key={balance.leaveType} sx={{ minWidth: { xs: 140, sm: 200 }, flex: { xs: '1 1 140px', sm: '1 1 200px' } }}>
                            <CardContent sx={{ px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    {balance.leaveType}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 0.5, sm: 2 } }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6">{balance.allocated}</Typography>
                                        <Typography data-help="leave.mine.balance.allocated" variant="caption" color="text.secondary">Allocated</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6">{balance.availed}</Typography>
                                        <Typography data-help="leave.mine.balance.used" variant="caption" color="text.secondary">Used</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" color={balance.remaining < 0 ? 'error' : 'success.main'}>
                                            {balance.remaining}
                                        </Typography>
                                        <Typography data-help="leave.mine.balance.remaining" variant="caption" color="text.secondary">Remaining</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            <Paper data-help-section="leave.mine.table">
                <ResponsiveTable<LeaveApplication>
                    pageKey="leave.mine"
                    columns={columns}
                    rows={leaveData.content}
                    getRowKey={r => r.id}
                    emptyMessage="No leave applications found"
                />
            </Paper>

            <LeaveForm
                open={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                onSubmitSuccess={fetchLeaveApplications}
            />

            <Dialog
                open={cancelConfirmOpen}
                PaperProps={{ 'data-help-page': 'leave.cancelForm.page' } as any}
                onClose={handleCancelDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Cancel Leave Application</DialogTitle>
                <DialogContent data-help-section="leave.cancelForm.section.message">
                    <Typography>
                        Are you sure you want to cancel this leave application?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.cancelForm.cancel" onClick={handleCancelDialogClose}>No</Button>
                    <Button
                        data-help="leave.cancelForm.confirm"
                        onClick={handleCancelConfirm}
                        variant="contained"
                        color="error"
                        autoFocus
                    >
                        Yes, Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveApplicationList;
